import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Locations from './pages/Locations';
import SubscriptionTypes from './pages/SubscriptionTypes';
import Subscriptions from './pages/Subscriptions';
import UsersPage from './pages/Users';
import Documents from './pages/Documents';
import EmailSettings from './pages/EmailSettings';
import Feedback from './pages/Feedback';
import AppSettings from './pages/AppSettings';
import DeleteAccount from './pages/DeleteAccount';
import CarCatalog from './pages/CarCatalog';
import SmsPage from './pages/SmsPage';
import EmailPage from './pages/Email';
import AppNotifications from './pages/AppNotifications';
import Facturare from './pages/Facturare';
import Diagnostics from './pages/Diagnostics';
import './App.css';

const ProtectedRoute = ({ children, adminOnly = false, viewerAllowed = false }) => {
  const { isAuthenticated, isAdmin, isViewer, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isViewer && !viewerAllowed) {
    return <Navigate to="/users" replace />;
  }

  if (adminOnly && !isAdmin && !isViewer) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isViewer, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={isViewer ? "/users" : "/dashboard"} replace />;
  }

  return children;
};

const DefaultRedirect = () => {
  const { isViewer } = useAuth();
  return <Navigate to={isViewer ? "/users" : "/dashboard"} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute adminOnly viewerAllowed>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations"
        element={
          <ProtectedRoute adminOnly>
            <Locations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-types"
        element={
          <ProtectedRoute adminOnly>
            <SubscriptionTypes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute adminOnly viewerAllowed>
            <Subscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute adminOnly>
            <Documents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-settings"
        element={
          <ProtectedRoute adminOnly>
            <EmailSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback"
        element={
          <ProtectedRoute adminOnly>
            <Feedback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app-settings"
        element={
          <ProtectedRoute adminOnly>
            <AppSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/car-catalog"
        element={
          <ProtectedRoute adminOnly>
            <CarCatalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sms"
        element={
          <ProtectedRoute adminOnly>
            <SmsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email"
        element={
          <ProtectedRoute adminOnly>
            <EmailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app-notifications"
        element={
          <ProtectedRoute adminOnly>
            <AppNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/facturare"
        element={
          <ProtectedRoute adminOnly>
            <Facturare />
          </ProtectedRoute>
        }
      />
      <Route
        path="/diagnostics"
        element={
          <ProtectedRoute adminOnly>
            <Diagnostics />
          </ProtectedRoute>
        }
      />
      <Route path="/stergere-cont" element={<DeleteAccount />} />
      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
